'use strict';

/**
 * GalleryPage -- the full-page view of the caller's PRIVATE media (their personal gallery: direct
 * uploads plus every image pasted into a note), rendered into the main workspace
 * (#Plansheet-Workspace) rather than the notebook rail. It reads through providers.MediaAPI.listMine
 * and keeps its own render state under AppData.GalleryPage so the rail notebook's Gallery tab and
 * this page never share a buffer.
 *
 * Three item kinds:
 *   Image  -- images (MimeType starts with image/); shown as thumbnail, click copies
 *             a markdown image reference  ![Title](url) to the clipboard.
 *   File   -- any other binary upload; shown as a download-link card, click copies
 *             a markdown link reference   [Title](url) to the clipboard.
 *   Link   -- external URL entries (MediaType === 'Link'); shown as an external-link
 *             card that opens in a new tab; click icon copies [Title](url).
 *
 * Upload is a visible button plus a hidden multi-file input. The button does NOT use a
 * <label for> over a display:none input -- that path is unreliable in mobile Safari.
 * The button's onclick calls openPicker(), which programmatically clicks the input.
 *
 * URLs are added via addURLDialog(), which opens a pict-section-modal (no native
 * prompt/alert). The URL is stored in OriginalFileName; MediaType is set to 'Link'.
 */

const libPictView = require('pict-view');

// Short human-readable label for a file card (e.g. "PDF", "XLSX").
function _fileTypeLabel(pMime, pFileName)
{
	let tmpMime = String(pMime || '').toLowerCase();
	if (tmpMime === 'application/pdf') { return 'PDF'; }
	if (tmpMime === 'application/zip' || tmpMime === 'application/x-zip-compressed') { return 'ZIP'; }
	if (tmpMime.indexOf('spreadsheet') >= 0 || tmpMime.indexOf('excel') >= 0 || tmpMime === 'text/csv') { return 'Spreadsheet'; }
	if (tmpMime.indexOf('wordprocessing') >= 0 || tmpMime.indexOf('msword') >= 0) { return 'Document'; }
	if (tmpMime.indexOf('presentation') >= 0 || tmpMime.indexOf('powerpoint') >= 0) { return 'Slides'; }
	if (tmpMime.indexOf('text/') === 0) { return 'Text'; }
	if (tmpMime.indexOf('video/') === 0) { return 'Video'; }
	if (tmpMime.indexOf('audio/') === 0) { return 'Audio'; }
	let tmpName = String(pFileName || '');
	let tmpDot = tmpName.lastIndexOf('.');
	if (tmpDot >= 0) { return tmpName.slice(tmpDot + 1).toUpperCase().slice(0, 8) || 'File'; }
	return 'File';
}

class PictViewGalleryPage extends libPictView
{
	_page() { return this.pict.AppData.GalleryPage || (this.pict.AppData.GalleryPage = { Rows: [] }); }
	_media() { return this.pict.providers.MediaAPI; }

	// Load (or refresh) the caller's private media, then repaint the page.
	show()
	{
		let tmpMedia = this._media();
		if (!tmpMedia || typeof tmpMedia.listMine !== 'function') { this.render(); return; }
		return tmpMedia.listMine()
			.then((pRows) => { this._page().Rows = Array.isArray(pRows) ? pRows : []; this.render(); })
			.catch(() => this.render());
	}

	onBeforeRender(pRenderable)
	{
		let tmpPage = this._page();
		let tmpMedia = this._media();
		let tmpRows = tmpPage.Rows || [];

		let tmpImages = [];
		let tmpFiles = [];
		let tmpLinks = [];

		tmpRows.forEach((pM) =>
		{
			let tmpIsLink = (pM.MediaType === 'Link');
			let tmpIsImage = !tmpIsLink && (String(pM.MimeType || '').indexOf('image/') === 0);
			let tmpURL = tmpMedia ? tmpMedia.blobURL(pM.IDMedia) : ('/1.0/Media/' + pM.IDMedia + '/Blob');
			let tmpTitle = pM.Title || pM.OriginalFileName || ('Media #' + pM.IDMedia);

			if (tmpIsLink)
			{
				tmpLinks.push({
					IDMedia: pM.IDMedia,
					Title: tmpTitle,
					URL: String(pM.OriginalFileName || '')
				});
			}
			else if (tmpIsImage)
			{
				tmpImages.push({
					IDMedia: pM.IDMedia,
					Title: tmpTitle,
					URL: tmpURL
				});
			}
			else
			{
				tmpFiles.push({
					IDMedia: pM.IDMedia,
					Title: tmpTitle,
					URL: tmpURL,
					TypeLabel: _fileTypeLabel(pM.MimeType, pM.OriginalFileName)
				});
			}
		});

		tmpPage.ImageRows = tmpImages;
		tmpPage.FileRows = tmpFiles;
		tmpPage.LinkRows = tmpLinks;
		// One-element array trick: section wrapper renders 0 or 1 times; nested {~TS:~}
		// inside each section template fills the items from AppData.
		tmpPage.HasImages = tmpImages.length ? [{}] : [];
		tmpPage.HasFiles = tmpFiles.length ? [{}] : [];
		tmpPage.HasLinks = tmpLinks.length ? [{}] : [];
		tmpPage.GalleryEmpty = (tmpImages.length + tmpFiles.length + tmpLinks.length) ? [] : [1];

		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// Mobile-Safari-safe file picker: programmatically click the hidden input instead of relying on a
	// <label for> over a display:none input (which silently does nothing on iOS Safari).
	openPicker()
	{
		if (typeof document === 'undefined') { return; }
		let tmpInput = document.getElementById('PS-GalleryPage-Input');
		if (tmpInput) { tmpInput.click(); }
	}

	onPick(pInput)
	{
		if (!pInput || !pInput.files) { return; }
		this._uploadFiles(pInput.files);
		pInput.value = '';
	}

	_uploadFiles(pFiles)
	{
		let tmpMedia = this._media();
		if (!tmpMedia) { return; }
		let tmpAll = [];
		for (let i = 0; i < pFiles.length; i++)
		{
			if (pFiles[i]) { tmpAll.push(pFiles[i]); }
		}
		if (!tmpAll.length) { return; }
		// OwnerType 'User' / IDOwner 0: a direct gallery upload (not tied to a note). Private: true is
		// what makes it the caller's own; the server stamps Media.IDUser from the session.
		Promise.all(tmpAll.map((pF) => tmpMedia.upload('User', 0, pF, { Private: true }).catch(() => null)))
			.then(() => this.show());
	}

	// Open a pict-section-modal dialog to add an external URL reference (no native prompt/alert).
	addURLDialog()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal) { return; }
		tmpModal.show(
		{
			title: 'Add a URL reference',
			content:
				'<p style="font-size:13px;">Saves a link to an external site in your gallery. It opens in a new tab; you can also copy a markdown reference for use in a note.</p>'
				+ '<label style="font-size:12px;">Label (optional)</label>'
				+ '<input id="PS-URL-Title" class="pict-input" style="width:100%; margin-bottom:8px;" placeholder="Descriptive name">'
				+ '<label style="font-size:12px;">URL</label>'
				+ '<input id="PS-URL-Href" class="pict-input" style="width:100%;" placeholder="https://...">',
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'save', Label: 'Add URL', Style: 'primary' }
			]
		}).then((pChoice) =>
		{
			if (pChoice !== 'save') { return; }
			let tmpURL = ((document.getElementById('PS-URL-Href') || {}).value || '').trim();
			let tmpTitle = ((document.getElementById('PS-URL-Title') || {}).value || '').trim() || tmpURL;
			if (!tmpURL) { return; }
			let tmpMedia = this._media();
			if (!tmpMedia || typeof tmpMedia.uploadURL !== 'function') { return; }
			tmpMedia.uploadURL('User', 0, tmpURL, tmpTitle, { Private: true })
				.then(() => this.show())
				.catch((pErr) => this._toast('Could not save URL: ' + (pErr.message || pErr), 'error'));
		});
	}

	// Copy the appropriate markdown reference for this media item to the clipboard.
	// Images  -> ![Title](url)
	// Files   -> [Title](url)
	// Links   -> [Title](externalURL)
	copyRef(pIDMedia)
	{
		let tmpMedia = this._media();
		let tmpPage = this._page();
		let tmpRows = tmpPage.Rows || [];
		let tmpRow = tmpRows.find((pM) => Number(pM.IDMedia) === Number(pIDMedia));
		let tmpIsLink = tmpRow && (tmpRow.MediaType === 'Link');
		let tmpIsImage = tmpRow && !tmpIsLink && (String(tmpRow.MimeType || '').indexOf('image/') === 0);
		let tmpTitle = (tmpRow && (tmpRow.Title || tmpRow.OriginalFileName)) || ('Media #' + pIDMedia);

		let tmpRef;
		if (tmpIsLink)
		{
			let tmpHref = (tmpRow && String(tmpRow.OriginalFileName || '')) || '';
			tmpRef = '[' + tmpTitle + '](' + tmpHref + ')';
		}
		else if (tmpIsImage)
		{
			let tmpURL = tmpMedia ? tmpMedia.blobURL(pIDMedia) : ('/1.0/Media/' + pIDMedia + '/Blob');
			tmpRef = '![' + tmpTitle + '](' + tmpURL + ')';
		}
		else
		{
			let tmpURL = tmpMedia ? tmpMedia.blobURL(pIDMedia) : ('/1.0/Media/' + pIDMedia + '/Blob');
			tmpRef = '[' + tmpTitle + '](' + tmpURL + ')';
		}

		if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText)
		{
			navigator.clipboard.writeText(tmpRef)
				.then(() => this._toast('Reference copied.', 'success'))
				.catch(() => this._toast(tmpRef, 'info'));
		}
		else { this._toast(tmpRef, 'info'); }
	}

	deleteItem(pIDMedia)
	{
		let tmpMedia = this._media();
		if (!tmpMedia || typeof tmpMedia.removeMine !== 'function') { return; }
		tmpMedia.removeMine(pIDMedia).then(() => this.show()).catch(() => {});
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2200 });
		}
	}
}

module.exports = PictViewGalleryPage;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-GalleryPage',
	DefaultRenderable: 'Plansheet-GalleryPage',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ps-gallerypage.ps-page { color: var(--theme-color-text-primary, #1d2230); }
		.ps-gallerypage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; flex-wrap: wrap; }
		.ps-gallerypage-head h2 { font-size: 22px; font-weight: 600; margin: 0; color: var(--theme-color-text-primary, #1f2430); }
		.ps-gallerypage-actions { display: flex; gap: 8px; margin-left: auto; flex-wrap: wrap; }
		.ps-gallerypage-upload { font: inherit; font-size: 13px; border: 1px solid var(--theme-color-brand-primary, #3b6fd4);
			background: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-text-on-brand, #fff); border-radius: 7px; padding: 6px 13px; cursor: pointer; }
		.ps-gallerypage-upload:hover { filter: brightness(1.06); }
		.ps-gallerypage-url { font: inherit; font-size: 13px; border: 1px solid var(--theme-color-border-default, #c8cdd8);
			background: transparent; color: var(--theme-color-text-primary, #1d2230); border-radius: 7px; padding: 6px 13px; cursor: pointer; }
		.ps-gallerypage-url:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-gallerypage-input { display: none; }
		.ps-gallerypage-section { margin-bottom: 28px; }
		.ps-gallerypage-section-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em;
			color: var(--theme-color-text-secondary, #8a93a6); margin-bottom: 10px; }
		.ps-gallerypage-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px; }
		.ps-gallerypage-item { position: relative; aspect-ratio: 1 / 1; border-radius: 10px; overflow: hidden;
			border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-secondary, #f1f3f7); }
		.ps-gallerypage-thumb { width: 100%; height: 100%; object-fit: cover; display: block; cursor: pointer; }
		.ps-gallerypage-del { position: absolute; top: 6px; right: 6px; width: 26px; height: 26px; border: none; border-radius: 6px;
			background: rgba(20, 24, 32, 0.55); color: #fff; cursor: pointer; font-size: 16px; line-height: 1;
			display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.15s ease; }
		.ps-gallerypage-item:hover .ps-gallerypage-del { opacity: 1; }
		.ps-gallerypage-del:hover { background: var(--theme-color-status-error, #cc3333); }
		.ps-gallerypage-filelist { display: flex; flex-direction: column; gap: 8px; }
		.ps-gallerypage-filerow { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 9px; background: var(--theme-color-background-secondary, #f1f3f7); cursor: pointer; min-width: 0; }
		.ps-gallerypage-filerow:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); background: var(--theme-color-background-hover, #eaf2ff); }
		.ps-gallerypage-filebadge { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
			background: var(--theme-color-background-panel, #fff); padding: 2px 7px; border-radius: 999px; flex-shrink: 0;
			border: 1px solid var(--theme-color-border-default, #d8dde6); color: var(--theme-color-text-secondary, #5b6376); }
		.ps-gallerypage-filetitle { font-size: 13px; color: var(--theme-color-text-primary, #1d2230); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
		.ps-gallerypage-filedel { background: none; border: none; cursor: pointer; color: var(--theme-color-text-secondary, #8a93a6); font-size: 18px; line-height: 1; flex-shrink: 0; padding: 0 2px; }
		.ps-gallerypage-filedel:hover { color: var(--theme-color-status-error, #cc3333); }
		.ps-gallerypage-linkrow { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 9px; background: var(--theme-color-background-secondary, #f1f3f7); min-width: 0; }
		.ps-gallerypage-linkhref { font-size: 13px; color: var(--theme-color-brand-primary, #3b6fd4); text-decoration: none; flex: 1;
			white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
		.ps-gallerypage-linkhref:hover { text-decoration: underline; }
		.ps-gallerypage-linkbadge { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
			background: var(--theme-color-background-panel, #fff); padding: 2px 7px; border-radius: 999px; flex-shrink: 0;
			border: 1px solid var(--theme-color-border-default, #d8dde6); color: var(--theme-color-text-secondary, #5b6376); }
		.ps-gallerypage-linkcopy { background: none; border: none; cursor: pointer; color: var(--theme-color-text-secondary, #8a93a6); font-size: 14px; line-height: 1; flex-shrink: 0; padding: 0 2px; }
		.ps-gallerypage-linkcopy:hover { color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-gallerypage-empty { color: var(--theme-color-text-secondary, #8a93a6); font-style: italic; font-size: 14px; padding: 14px 2px; }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-GalleryPage',
			Template: /*html*/`
<div class="ps-page ps-gallerypage">
	<div class="ps-gallerypage-head">
		<h2>Gallery</h2>
		<div class="ps-gallerypage-actions">
			<input type="file" id="PS-GalleryPage-Input" class="ps-gallerypage-input" multiple onchange="_Pict.views['Plansheet-GalleryPage'].onPick(this)" />
			<button class="ps-gallerypage-upload" onclick="_Pict.views['Plansheet-GalleryPage'].openPicker()">+ Upload file</button>
			<button class="ps-gallerypage-url" onclick="_Pict.views['Plansheet-GalleryPage'].addURLDialog()">+ Add URL</button>
		</div>
	</div>
	{~TS:GalleryPage-Empty:AppData.GalleryPage.GalleryEmpty~}
	{~TS:GalleryPage-ImagesSection:AppData.GalleryPage.HasImages~}
	{~TS:GalleryPage-FilesSection:AppData.GalleryPage.HasFiles~}
	{~TS:GalleryPage-LinksSection:AppData.GalleryPage.HasLinks~}
</div>`
		},
		{
			Hash: 'GalleryPage-Empty',
			Template: /*html*/`<p class="ps-gallerypage-empty">No files yet. Upload any file or add a URL; images pasted into a note land here too. Everything here is private to you.</p>`
		},
		{
			Hash: 'GalleryPage-ImagesSection',
			Template: /*html*/`<div class="ps-gallerypage-section"><div class="ps-gallerypage-section-label">Images</div><div class="ps-gallerypage-grid">{~TS:GalleryPage-ImageItem:AppData.GalleryPage.ImageRows~}</div></div>`
		},
		{
			Hash: 'GalleryPage-ImageItem',
			Template: /*html*/`<div class="ps-gallerypage-item"><img class="ps-gallerypage-thumb" src="{~D:Record.URL~}" alt="{~D:Record.Title~}" title="{~D:Record.Title~} -- click to copy a markdown reference" onclick="_Pict.views['Plansheet-GalleryPage'].copyRef({~D:Record.IDMedia~})" /><button class="ps-gallerypage-del" title="Delete" onclick="_Pict.views['Plansheet-GalleryPage'].deleteItem({~D:Record.IDMedia~})">&times;</button></div>`
		},
		{
			Hash: 'GalleryPage-FilesSection',
			Template: /*html*/`<div class="ps-gallerypage-section"><div class="ps-gallerypage-section-label">Files</div><div class="ps-gallerypage-filelist">{~TS:GalleryPage-FileRow:AppData.GalleryPage.FileRows~}</div></div>`
		},
		{
			Hash: 'GalleryPage-FileRow',
			Template: /*html*/`<div class="ps-gallerypage-filerow" onclick="_Pict.views['Plansheet-GalleryPage'].copyRef({~D:Record.IDMedia~})" title="Click to copy a markdown download link"><span class="ps-gallerypage-filebadge">{~D:Record.TypeLabel~}</span><span class="ps-gallerypage-filetitle">{~D:Record.Title~}</span><button class="ps-gallerypage-filedel" title="Delete" onclick="event.stopPropagation(); _Pict.views['Plansheet-GalleryPage'].deleteItem({~D:Record.IDMedia~})">&times;</button></div>`
		},
		{
			Hash: 'GalleryPage-LinksSection',
			Template: /*html*/`<div class="ps-gallerypage-section"><div class="ps-gallerypage-section-label">Links</div><div class="ps-gallerypage-filelist">{~TS:GalleryPage-LinkRow:AppData.GalleryPage.LinkRows~}</div></div>`
		},
		{
			Hash: 'GalleryPage-LinkRow',
			Template: /*html*/`<div class="ps-gallerypage-linkrow"><span class="ps-gallerypage-linkbadge">URL</span><a class="ps-gallerypage-linkhref" href="{~D:Record.URL~}" target="_blank" rel="noopener" title="{~D:Record.URL~}">{~D:Record.Title~}</a><button class="ps-gallerypage-linkcopy" onclick="_Pict.views['Plansheet-GalleryPage'].copyRef({~D:Record.IDMedia~})" title="Copy markdown reference">{~I:Copy~}</button><button class="ps-gallerypage-filedel" title="Delete" onclick="_Pict.views['Plansheet-GalleryPage'].deleteItem({~D:Record.IDMedia~})">&times;</button></div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-GalleryPage',
			TemplateHash: 'Plansheet-GalleryPage',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
